import React, {
	useCallback,
	useEffect,
	createContext,
	useContext,
	type ReactNode,
	type ReactElement,
} from 'react';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useFlagsService } from '@atlassian/jira-flags';
import { useHelpPanelLink } from '@atlassian/jira-help-panel-button/src/index.tsx';
import { createSessionStorageProvider } from '@atlassian/jira-browser-storage-providers/src/controllers/session-storage/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { LEARN_MORE_IPH_ARTICLE_ID } from '../../common/constants.tsx';
import { AsyncSpotlightCard } from '../../common/ui/spotlight/index.tsx';
import type { SpotlightCardProps } from '../../common/ui/spotlight/spotlight-card/index.tsx';
import SpotlightTarget, {
	type SpotlightTargetProps,
} from '../../common/ui/spotlight/spotlight-target/index.tsx';

import messages from './messages.tsx';

const sessionStorageProvider = createSessionStorageProvider('', false);

export const NinChangeboardingContext = createContext(false);

export const EnableNinChangeboardingProvider = ({ children }: { children: ReactNode }) => (
	<NinChangeboardingContext.Provider value>{children}</NinChangeboardingContext.Provider>
);

// Remove this on clean up of 'remove_nin_changeboarding_rll_usages'
export const NinChangeboardingTourTarget = ({ children, engagementId }: SpotlightTargetProps) => {
	const isTourEnabled = useContext(NinChangeboardingContext);
	if (!isTourEnabled || fg('remove_nin_changeboarding_rll_usages')) {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		return children as ReactElement;
	}
	return <SpotlightTarget engagementId={engagementId}>{children}</SpotlightTarget>;
};

// Remove on clean up of 'remove_nin_changeboarding_rll_usages'
export const NinChangeboardingTourCard = ({
	mode,
	view,
	hasIssueResults,
	isAiEnabled,
}: SpotlightCardProps): React.JSX.Element | null => {
	const isTourEnabled = useContext(NinChangeboardingContext);

	return !isTourEnabled || fg('remove_nin_changeboarding_rll_usages') ? null : (
		<AsyncSpotlightCard
			mode={mode}
			view={view}
			hasIssueResults={hasIssueResults}
			isAiEnabled={isAiEnabled}
		/>
	);
};

export const useGlobalScopeOptInFlag = (): void => {
	const isFlagEnabled = useContext(NinChangeboardingContext);
	const { showFlag } = useFlagsService();
	const onOpenInProductHelpArticle = useHelpPanelLink();

	const onLearnMore = useCallback(() => {
		onOpenInProductHelpArticle(LEARN_MORE_IPH_ARTICLE_ID);
	}, [onOpenInProductHelpArticle]);

	useEffect(() => {
		const shouldShowOptInFlag = fg('jfp-vulcan-browser-storage-migration')
			? sessionStorageProvider.get('showNinOptInFlag')
			: sessionStorage.getItem('showNinOptInFlag');
		if (isFlagEnabled && shouldShowOptInFlag === 'true') {
			showFlag({
				type: 'success',
				title: expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
					? messages.successOptInTitleIssueTermRefresh
					: messages.successOptInTitle,
				description: expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
					? messages.successOptInDescIssueTermRefresh
					: messages.successOptInDesc,
				messageId:
					'issue-navigator-changeboarding.controllers.enable-nin-changeboarding.show-flag.success',
				messageType: 'transactional',
				actions: [
					{
						content: messages.successActionLearnMoreLink,
						onClick: onLearnMore,
					},
				],
				isAutoDismiss: false,
				testId: 'issue-navigator-changeboarding.optin-success-flag',
			});
			fg('jfp-vulcan-browser-storage-migration')
				? sessionStorageProvider.remove('showNinOptInFlag')
				: sessionStorage.removeItem('showNinOptInFlag');
		}
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
};
